import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    Users,
    UserCheck,
    UserX,
    Clock,
    TrendingUp,
    AlertTriangle,
    RefreshCw,
    ChevronLeft,
    Activity,
    Timer,
    Zap,
    AlertCircle,
    XCircle,
    UserMinus,
    ChevronDown
} from 'lucide-react';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import { toast } from 'react-hot-toast';
import { formatIndianNumber } from '../../../utils/amountFormatter';

const AttendanceKpiDashboard = ({ onBack }) => {
    // 'custom' = use manual date inputs
    // '7', '15', '30', etc. = preset ranges
    const [timeFilter, setTimeFilter] = useState('today');

    // Default to today
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

    const [isRefreshing, setIsRefreshing] = useState(false);

    // Update dates based on filter selection
    useEffect(() => {
        if (timeFilter === 'custom') return;

        const end = new Date();
        const start = new Date();

        if (timeFilter !== 'today') {
            const daysToSubtract = parseInt(timeFilter) - 1;
            start.setDate(end.getDate() - daysToSubtract);
        }

        setStartDate(start.toISOString().split('T')[0]);
        setEndDate(end.toISOString().split('T')[0]);
    }, [timeFilter]);

    // Fetch dashboard data using React Query
    const {
        data: dashboardData,
        isLoading,
        error,
        refetch
    } = useQuery({
        queryKey: ['attendance-kpi-dashboard', startDate, endDate],
        queryFn: async () => {
            const url = `${process.env.REACT_APP_API_BASE_URL}/attendance/dashboard?startDate=${startDate}&endDate=${endDate}`;

            const response = await authFetch(url, {
                credentials: 'include',
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                let errorMsg = 'Failed to fetch dashboard data';
                try {
                    const errorData = await response.json();
                    errorMsg = errorData?.responseMessage || errorData?.message || errorMsg;
                } catch (e) {
                    // Non-JSON error response
                }
                throw new Error(errorMsg);
            }

            const result = await response.json();

            if (result.success) {
                return result.data;
            } else {
                throw new Error(result.responseMessage || result.message || 'Failed to fetch dashboard data');
            }
        },
        staleTime: 0,
        refetchOnMount: true
    });

    // Notify backend error via toast instead of blocking the page
    useEffect(() => {
        if (error) {
            toast.error(error?.message || 'Failed to fetch dashboard data');
        }
    }, [error]);

    // Handle refresh
    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            const res = await refetch();
            if (res.isError) {
                toast.error(res.error?.message || 'Failed to refresh dashboard');
            } else {
                toast.success('Dashboard refreshed successfully');
            }
        } catch (err) {
            toast.error(err?.message || 'Failed to refresh dashboard');
        } finally {
            setIsRefreshing(false);
        }
    };

    // Handle filter change
    const handleFilterChange = (e) => {
        setTimeFilter(e.target.value);
    };

    // Format date for display
    const formatDisplayDate = (startStr, endStr) => {
        const options = { month: 'short', day: 'numeric', year: 'numeric' };
        const start = new Date(startStr).toLocaleDateString('en-US', options);
        const end = new Date(endStr).toLocaleDateString('en-US', options);
        return start === end ? start : `${start} - ${end}`;
    };

    // KPI Card Component
    const KpiCard = ({ icon: Icon, title, value, subtitle, color, trend }) => (
        <div className="relative overflow-hidden bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group">
            {/* Background Accent */}
            <div className={`absolute -right-6 -top-6 h-32 w-32 rounded-full ${color} opacity-5 group-hover:opacity-10 transition-opacity duration-300`}></div>

            <div className="relative z-10">
                {/* Header: Icon + Title */}
                <div className="flex items-center gap-3 mb-4">
                    <div className={`p-2 rounded-lg ${color} shadow-md transform group-hover:scale-105 transition-transform duration-300`}>
                        <Icon className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider">{title}</h3>
                </div>

                {/* Value */}
                <div className="flex items-center justify-between">
                    <p className="text-2xl font-black text-gray-800 tracking-tight">{value}</p>
                    {trend && (
                        <span className={`text-xs px-2 py-1 rounded-full ${trend.color} font-bold shadow-sm`}>
                            {trend.label}
                        </span>
                    )}
                </div>

                {subtitle && (
                    <div className="mt-3 flex items-center">
                        <div className={`h-1.5 w-1.5 rounded-full ${color} mr-2`}></div>
                        <p className="text-xs font-bold text-gray-500 bg-gray-50 px-2 py-1 rounded-md">{subtitle}</p>
                    </div>
                )}
            </div>
        </div>
    );

    // Section Header Component
    const SectionHeader = ({ icon: Icon, title, color }) => (
        <div className="flex items-center gap-3 mb-6">
            <div className={`p-2 rounded-lg ${color}`}>
                <Icon className="h-5 w-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="mx-auto space-y-6">
                {/* Header */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex items-center gap-4">
                            {onBack && (
                                <button
                                    onClick={onBack}
                                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                    aria-label="Back to previous page"
                                >
                                    <ChevronLeft className="h-5 w-5 text-gray-600" />
                                </button>
                            )}
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900 mb-1">
                                    Attendance KPI Dashboard
                                </h1>
                                <p className="text-sm text-gray-500">
                                    {startDate && endDate
                                        ? formatDisplayDate(startDate, endDate)
                                        : 'Select Date Range'}
                                </p>
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-3">
                            {/* Time Range Filter */}
                            <div className="relative">
                                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
                                <select
                                    value={timeFilter}
                                    onChange={handleFilterChange}
                                    className="appearance-none bg-gray-50 pl-4 pr-10 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
                                >
                                    <option value="custom">Custom Range</option>
                                    <option value="today">Today</option>
                                    <option value="7">Last 7 Days</option>
                                    <option value="15">Last 15 Days</option>
                                    <option value="30">Last 1 Month</option>
                                    <option value="90">Last 3 Months</option>
                                    <option value="180">Last 6 Months</option>
                                    <option value="365">Last 1 Year</option>
                                </select>
                            </div>

                            {/* Date Inputs - Only shown when Custom Range is selected */}
                            {timeFilter === 'custom' && (
                                <div className="flex-shrink-0">
                                    <DateRangeFilter
                                        startDate={startDate}
                                        endDate={endDate}
                                        onChange={(range) => {
                                            setStartDate(range.startDate);
                                            setEndDate(range.endDate);
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Loading State */}
                {isLoading ? (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12">
                        <div className="flex flex-col items-center justify-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600 mb-4"></div>
                            <p className="text-gray-600 font-medium">Loading dashboard data...</p>
                        </div>
                    </div>
                ) : dashboardData ? (
                    <>
                        {/* 🟢 Workforce Snapshot */}
                        <div className="p-6">
                            <SectionHeader
                                icon={Users}
                                title="Workforce Snapshot"
                                color="bg-green-500"
                            />
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                <KpiCard
                                    icon={Users}
                                    title="Total Staff"
                                    value={formatIndianNumber(dashboardData.workforceSnapshot.totalEmployees)}
                                    color="bg-blue-600"
                                />
                                <KpiCard
                                    icon={UserCheck}
                                    title="Active Staff"
                                    value={formatIndianNumber(dashboardData.workforceSnapshot.activeStaff)}
                                    color="bg-emerald-600"
                                />
                                <KpiCard
                                    icon={UserX}
                                    title="Inactive Staff"
                                    value={formatIndianNumber(dashboardData.workforceSnapshot.inactiveStaff)}
                                    color="bg-orange-600"
                                />
                                <KpiCard
                                    icon={XCircle}
                                    title="Exit Staff"
                                    value={formatIndianNumber(dashboardData.workforceSnapshot.exitStaff)}
                                    color="bg-rose-600"
                                />
                                <KpiCard
                                    icon={Activity}
                                    title="Present Today"
                                    value={formatIndianNumber(dashboardData.workforceSnapshot.presentToday)}
                                    subtitle={`${dashboardData.workforceSnapshot.attendancePercentage}% attendance`}
                                    color="bg-green-500"
                                />
                                <KpiCard
                                    icon={AlertCircle}
                                    title="Absent Today"
                                    value={formatIndianNumber(dashboardData.workforceSnapshot.absentToday)}
                                    color="bg-red-500"
                                />
                                <KpiCard
                                    icon={UserMinus}
                                    title="On Leave"
                                    value={formatIndianNumber(dashboardData.workforceSnapshot.onLeave)}
                                    color="bg-purple-500"
                                />
                                <KpiCard
                                    icon={TrendingUp}
                                    title="Attendance %"
                                    value={`${dashboardData.workforceSnapshot.attendancePercentage}%`}
                                    color="bg-indigo-500"
                                />
                            </div>
                        </div>

                        {/* 🟡 Discipline Metrics */}
                        <div className="p-6">
                            <SectionHeader
                                icon={AlertTriangle}
                                title="Discipline Metrics"
                                color="bg-yellow-500"
                            />
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                                <KpiCard
                                    icon={AlertCircle}
                                    title="Late Arrivals"
                                    value={formatIndianNumber(dashboardData.disciplineMetrics.lateArrivals)}
                                    color="bg-yellow-500"
                                />
                                <KpiCard
                                    icon={Clock}
                                    title="Early Exits"
                                    value={formatIndianNumber(dashboardData.disciplineMetrics.earlyExits)}
                                    color="bg-orange-500"
                                />
                                <KpiCard
                                    icon={XCircle}
                                    title="Missed Punch"
                                    value={formatIndianNumber(dashboardData.disciplineMetrics.missedPunch)}
                                    color="bg-red-500"
                                />
                                <KpiCard
                                    icon={Clock}
                                    title="Avg Check-In"
                                    value={dashboardData.disciplineMetrics.avgCheckInTime}
                                    color="bg-blue-500"
                                />
                                <KpiCard
                                    icon={Timer}
                                    title="Avg Working Hours"
                                    value={dashboardData.disciplineMetrics.avgWorkingHours}
                                    color="bg-green-500"
                                />
                            </div>
                        </div>

                        {/* 🔵 Work Activity */}
                        <div className="p-6">
                            <SectionHeader
                                icon={Activity}
                                title="Work Activity"
                                color="bg-blue-500"
                            />
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                                <KpiCard
                                    icon={Timer}
                                    title="Total Working Hours"
                                    value={dashboardData.workActivity.totalWorkingHours}
                                    color="bg-green-500"
                                />
                                <KpiCard
                                    icon={AlertCircle}
                                    title="Employees < 4 Hours"
                                    value={formatIndianNumber(dashboardData.workActivity.employeesLessThan4Hours)}
                                    color="bg-orange-500"
                                />
                                <KpiCard
                                    icon={Zap}
                                    title="Employees > 9 Hours"
                                    value={formatIndianNumber(dashboardData.workActivity.employeesMoreThan9Hours)}
                                    color="bg-purple-500"
                                />
                                <KpiCard
                                    icon={TrendingUp}
                                    title="Overtime Count"
                                    value={formatIndianNumber(dashboardData.workActivity.overtimeCount)}
                                    color="bg-indigo-500"
                                />
                                <KpiCard
                                    icon={UserX}
                                    title="Yet to Check-In"
                                    value={formatIndianNumber(dashboardData.workActivity.employeesYetToCheckIn)}
                                    color="bg-gray-500"
                                />
                            </div>
                        </div>
                    </>
                ) : null}
            </div>
        </div>
    );
};

export default AttendanceKpiDashboard;
